import { AfterViewInit, Component, ElementRef, OnInit } from '@angular/core';
import { IntegrationsInitOptions } from '@segment/analytics-next/dist/types/browser/settings';
import { getItemAsJson, setItemAsJson } from 'helpers';
import * as _ from 'lodash';
import { WindowService } from 'shared/services';
import { SegmentService } from 'shared/services/segment.service';
import { usercentricsDraftPreview, usercentricsSettingsId } from 'src/app/config/usercentrics';

@Component({
    selector: 'fc-usercentrics',
    template: ''
})
export class UsercentricsComponent implements OnInit, AfterViewInit {
    private consentStorageKey = 'usercentrics-consent-given';
    private consentGiven = getItemAsJson(this.consentStorageKey);
    private segmentLoaded = false;

    constructor(
        private elementRef: ElementRef,
        private windowService: WindowService,
        private segmentService: SegmentService
    ) {}

    ngOnInit() {
        // Listen for consent change and load Segment once after user has given initial consent
        this.windowService.addEventListener('UC_CONSENT', this.handleConsentChange.bind(this));
    }

    ngAfterViewInit() {
        this.loadUsercentrics(this.elementRef.nativeElement);
    }

    private handleConsentChange(event: CustomEvent) {
        if (this.segmentLoaded) {
            return;
        }

        const detail = event.detail as ConsentDetails;
        const consent = detail.consent;

        // When user has not given consent before, we wait for their input and not load segment on initial page load.
        if (consent.updatedBy === 'onInitialPageLoad' && !this.consentGiven) {
            return;
        }

        const integrations: IntegrationsInitOptions = { All: false };

        // Build integrations map
        switch (consent.status) {
            case 'ALL_ACCEPTED':
                integrations.All = true;
                break;

            case 'SOME_ACCEPTED':
                _.forEach(detail.services, (service) => {
                    integrations[service.name] = !!(service.consent?.given && service.consent.type === 'EXPLICIT');
                });
                break;

            case 'ALL_DENIED':
                integrations.All = false;
                break;
        }

        // Only load analytics once
        this.segmentService.load({ integrations });
        this.segmentLoaded = true;

        if (!this.consentGiven) {
            // TODO: Handle this in user service after refactor
            setItemAsJson(this.consentStorageKey, true);
            this.consentGiven = true;
        }
    }

    private loadUsercentrics(element: HTMLElement) {
        const script = document.createElement('script');
        script.id = 'usercentrics-cmp';
        // Usercentrics v3 that matches the version used in GTM
        script.src = 'https://web.cmp.usercentrics.eu/ui/loader.js';
        script.setAttribute('data-settings-id', usercentricsSettingsId);
        if (usercentricsDraftPreview) script.setAttribute('data-draft', 'true');
        script.async = true;
        element.appendChild(script);
    }
}

// https://usercentrics.com/docs/web/features/api/types/
interface ConsentDetails {
    consent: ConsentData;
    services: Record<string, ServiceData>;
    categories: Record<string, CategoryData>;
}

interface ConsentData {
    status: 'ALL_ACCEPTED' | 'ALL_DENIED' | 'SOME_ACCEPTED' | 'SOME_DENIED';
    serviceIds?: string[];
    required: boolean;
    version: number;
    controllerId: string;
    language: string;
    createdAt: number;
    updatedAt: number;
    updatedBy: ConsentActionType;
    setting: SettingData;
    type: ConsentType;
    hash: string;
    gpcSignal?: boolean;
    isBot?: true;
    isOutsideEu?: true;
}

interface SettingData {
    id: string;
    type: SettingType;
    version: string;
    abVariant?: string;
    sandbox?: true;
}

interface ServiceData {
    name: string;
    version: string;
    category: string;
    essential: boolean;
    consent?: {
        given: boolean;
        type: 'IMPLICIT' | 'EXPLICIT';
    };
    gcm?: {
        analyticsStorage?: true;
        adStorage?: true;
    };
    subservices?: Record<string, ServiceData>;
    thirdCountryDataTransfer?: boolean;
    status?: 'added';
}

interface CategoryData {
    dps: Record<string, boolean> | null;
    essential?: boolean;
    hidden?: boolean;
    name: string;
    state: 'ALL_DENIED' | 'SOME_ACCEPTED' | 'ALL_ACCEPTED';
}

type ConsentType = 'IMPLICIT' | 'EXPLICIT';
type ConsentActionType =
    | 'onAcceptAllServices'
    | 'onDenyAllServices'
    | 'onEssentialChange'
    | 'onInitialPageLoad'
    | 'onNonEURegion'
    | 'onSessionRestored'
    | 'onTcfStringChange'
    | 'onUpdateServices'
    | 'onMobileSessionRestore';
type SettingType = 'TCF' | 'GDPR' | 'CCPA';
